import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import JSZip from 'jszip';
import './index.css'; // Make sure this import exists
import { Logo } from '../../configs';
import { API_TOKEN, DEPLOYED_APP_URL, get, put } from '../../configs/api';
import { ZoomIn, ZoomOut } from 'lucide-react';


function GEGDashboard() {
    const location = useLocation();
    const navigate = useNavigate();
    const isAuthenticated = useIsAuthenticated();
    const { instance } = useMsal();
    const username = location.state?.username;
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('pending');
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [userId, setUserId] = useState(null);
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [reportStartDate, setReportStartDate] = useState('');
    const [reportEndDate, setReportEndDate] = useState('');
    const profileMenuRef = useRef(null);
    const PAGE_SIZE = 10;
    const { accounts } = useMsal();
    const user = isAuthenticated && accounts?.length > 0 ? accounts?.[0] : null;
    // Display name: from location state (post-login) or MSAL account
    const displayName = username || user?.name || user?.idTokenClaims?.name || user?.username || user?.idTokenClaims?.preferred_username || 'User';
    const userEmail = user?.idTokenClaims?.email || user?.username || '';

    const getDocByType = (dist, type) => dist.license_documents.find(d => d.document_license_type === type);
    // Flatten data: One row per document for the table
    const reviewQueue = useMemo(() => {
        return data.flatMap(dist => dist.license_documents.map(doc => ({
            ...doc,
            distributor_name: dist.distributor_name,
            state_name: dist.state_name
        })));
    }, [data]);

    const setmyUserId = (userId) => {
        console.log(userId);
        setUserId(userId);
    };


    useEffect(() => {

        if (user) {
            const cwid = user?.idTokenClaims?.["https://bayer.com/cwid"];
            if (cwid) {
                setmyUserId(cwid);
            }
        } else {
            navigate("/login");
        }
    }, [user]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
                setProfileMenuOpen(false);
            }
        };
        if (profileMenuOpen) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => document.removeEventListener('click', handleClickOutside);
    }, [profileMenuOpen]);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await get(`/admin/distributors/filter?page=${currentPage}&size=${PAGE_SIZE}&document_status=UNDER_REVIEW`);
                if (response && response.items) {
                   
                    setData(response.items);
                    setTotalPages(Math.ceil(response.total / PAGE_SIZE));
                } else {
                    setData([]);
                    setTotalPages(0);
                }
            } catch (error) {
                console.error("Failed to fetch distributor data:", error);
                setData([]);
                setTotalPages(0);
            } finally {
                setIsLoading(false);
            }
        };

        if (activeTab === 'pending') {
            fetchData();
        } else {
            setData([]);
            setIsLoading(false);
        }
    }, [currentPage, activeTab]);

    const handleLogout = async () => {
        try {
            // Clear active account and local token state first
            instance.setActiveAccount(null);

            await instance.logoutPopup({
                postLogoutRedirectUri: "/login",
                mainWindowRedirectUri: "/unautherized"
            });

            navigate('/login');
        } catch (error) {
            // Fallback to redirect logout if popup fails
            try {
                await instance.logoutRedirect({
                    postLogoutRedirectUri: "/login",
                });
            } catch (redirectErr) {
                console.error('Logout failed', redirectErr);
            }
        }
    };

    const handleDownloadReviewedReport = async () => {
        if (!reportStartDate || !reportEndDate) return;
        try {
            const params = new URLSearchParams({
                start_date: reportStartDate,
                end_date: reportEndDate,
            });
            const url = `/admin/distributors/export/reviewed?${params.toString()}`;
            // The `get` helper is for JSON responses. For file downloads, we use `fetch` directly
            // to handle the raw response body as a blob.
            const test = get(url);

            // const res = await fetch(url, {
            //     method: "GET",
            //     headers: {
            //         "Content-Type": "application/json",
            //         "x-api-key": API_TOKEN,
            //     }
            // });
            // console.log(res,'---------')
            // if (!res.ok) {
            //     throw new Error(`Download failed (${res.status})`);
            // }
            // const blob = await res.blob();
            // const downloadUrl = window.URL.createObjectURL(blob);
            // const a = document.createElement('a');
            // a.href = downloadUrl;
            // a.download = `reviewed_licenses_${reportStartDate}_to_${reportEndDate}.csv`;
            // document.body.appendChild(a);
            // a.click();
            // a.remove();
            // window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error('Failed to download reviewed report', err);
            alert('Download failed. Please try again.');
        }
    };

    const handleAction = (docId, status, reason = null) => {

        // Create the new state based on the action
        let newData = data.map(dist => ({
            ...dist,
            license_documents: dist.license_documents.map(doc =>
                doc.id === docId ? { ...doc, approval_status: status, rejected_reason: reason } : doc
            )
        }));

        // Find the distributor that was just updated in the new data
        const distributor = newData.find(dist =>
            dist.license_documents.some(d => d.id === docId)
        );

        if (distributor) {
            // Find if there's another document in the same distributor that is still under review
            const nextPendingDocInRow = distributor.license_documents.find(
                doc => doc.approval_status === 'UNDER_REVIEW'
            );

            if (!nextPendingDocInRow) {
                newData = newData.filter(d => d !== distributor);
            }
        }

        // API call to update status
        const updateLicenseStatus = async () => {
            try {
                await put(`/admin/distributors/license/${docId}/review?documentId=${docId}`, { status: status, rejected_reason: reason, reviewed_by: userId });
                console.log("License status updated successfully");
            } catch (error) {
                console.error("Failed to update license status:", error);
                // Handle error appropriately, maybe show an error message to the user
            }
        };

        updateLicenseStatus();

        // Batch state updates
        setData(newData);
        setSelectedDoc(null);
    };

    return (
        <div className="dashboard-container">
            <header className="header-review">
                <img src={Logo} height={40} alt="Logo" />
                <span className="header-review-title"><strong>Review Distributors License Portal</strong></span>
                {user && (
                    <div className="user-profile-section" ref={profileMenuRef}>
                        <button
                            type="button"
                            className="user-profile-trigger"
                            onClick={() => setProfileMenuOpen((prev) => !prev)}
                            aria-expanded={profileMenuOpen}
                            aria-haspopup="true"
                            aria-label="User menu"
                        >
                            <div className="profile-circle" aria-hidden>
                                {(() => {
                                    const parts = String(displayName).trim().split(/\s+/).filter(Boolean);
                                    if (parts.length === 0) return '?';
                                    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
                                    return (parts[0][0] + parts[1][0]).toUpperCase();
                                })()}
                            </div>
                            <div className="user-profile-details">
                                <span className="user-profile-name">{displayName}</span>
                                {userEmail && <span className="user-profile-email">{userEmail}</span>}
                            </div>
                            <svg className={`user-profile-chevron ${profileMenuOpen ? 'open' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M7 10l5 5 5-5H7z" fill="currentColor" /></svg>
                        </button>
                        {profileMenuOpen && (
                            <div className="user-profile-menu">
                                <button type="button" className="user-profile-menu-item" onClick={() => { setProfileMenuOpen(false); handleLogout(); }} aria-label="Logout">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h12V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h12v-2H4V5z" fill="currentColor" /></svg>
                                    Logout
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </header>

            <main className="content">
                <div className="tabs">
                    <button
                        className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
                        onClick={() => {
                            setActiveTab('pending');
                            setCurrentPage(1);
                        }}
                    >Pending for Review</button>
                    <button
                        className={`tab-btn ${activeTab === 'reviewed' ? 'active' : ''}`}
                        onClick={() => {
                            setActiveTab('reviewed');
                            setCurrentPage(1);
                        }}
                    >Reviewed</button>
                </div>
                {isLoading ? (
                    <div>Loading...</div>
                ) : activeTab === 'reviewed' ? (
                    <div className="report-controls">
                        <div className="filter-group">
                            <input
                                type="date"
                                value={reportStartDate}
                                onChange={(e) => setReportStartDate(e.target.value)}
                            />
                            <span>to</span>
                            <input
                                type="date"
                                value={reportEndDate}
                                onChange={(e) => setReportEndDate(e.target.value)}
                            />
                            {/* <select>
                                <option value="">All Statuses</option>
                                <option value="APPROVED">Approved</option>
                                <option value="REJECTED">Rejected</option>
                            </select> */}
                        </div>
                        <button
                            className="download-btn"
                            onClick={handleDownloadReviewedReport}
                            disabled={!reportStartDate || !reportEndDate}
                        >
                            Download Report
                        </button>
                    </div>) : (
                    data.length > 0 ? (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Customer Id's</th>
                                    <th>Distributor</th>
                                    <th>State</th>
                                    <th>PAN Number</th>
                                    <th>Seed License</th>
                                    <th>Pesticide License</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map(dist => (
                                    <tr key={dist.tax_number}>
                                        <td>{dist.sap_ids.join(', ')}</td>
                                        <td>{dist.distributor_name}</td>
                                        <td>{dist.state_name}</td>
                                        <td>{dist.tax_number}</td>
                                        {/* Seed Column */}
                                        <td><LicenseCell dist={dist} type="SEED" onReview={(doc) => setSelectedDoc({
                                            ...doc,
                                            distributor_name: dist.distributor_name,
                                            state_name: dist.state_name,
                                            state_code: dist.state_code,
                                            sap_ids: dist.sap_ids,
                                            tax_number: dist.tax_number,
                                            license_issue_date: doc.license_issue_date,
                                            license_expiry_date: doc.license_expiry_date,
                                            document_urls: [...(doc.document_urls || []), ...(doc.document_acknowledgement_urls || [])]
                                        })} /></td>
                                        {/* Pesticide Column */}
                                        <td><LicenseCell dist={dist} type="PESTICIDE" onReview={(doc) => setSelectedDoc({
                                            ...doc,
                                            distributor_name: dist.distributor_name,
                                            state_name: dist.state_name,
                                            state_code: dist.state_code,
                                            sap_ids: dist.sap_ids,
                                            tax_number: dist.tax_number,
                                            license_issue_date: doc.license_issue_date,
                                            license_expiry_date: doc.license_expiry_date,
                                            document_urls: [...(doc.document_urls || []), ...(doc.document_acknowledgement_urls || [])]
                                        })} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="no-data-container">
                            <h3>No licenses to review</h3>
                            <p>There are currently no licenses awaiting review.</p>
                        </div>
                    )
                )}
                {activeTab === 'pending' && !isLoading && totalPages > 1 && (
                    <div className="pagination-controls">
                        <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
                            Previous
                        </button>
                        <span>Page {currentPage} of {totalPages}</span>
                        <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages}>
                            Next
                        </button>
                    </div>
                )}
            </main>

            {selectedDoc && (
                <Modal
                    doc={selectedDoc}
                    onClose={() => setSelectedDoc(null)}
                    onAction={handleAction}
                />
            )}
        </div>
    );
}
function LicenseCell({ dist, type, onReview }) {
    const isApplicable = type === 'SEED'
        ? dist.is_seed_business_distributor
        : dist.is_pesticide_business_distributor;
    const doc = dist.license_documents?.find(d => d.document_license_type === type) ?? null;

    if (!isApplicable) {
        return <span className="license-cell license-cell--na">Not applicable</span>;
    }
    if (isApplicable && !doc) {
        return (
            <span className="license-cell license-cell--upload-pending" title="Contact distributor to upload this license">
                Request distributor to upload
            </span>
        );
    }
    if (!doc) return <span className="license-cell">—</span>;
    if (doc.approval_status === 'UNDER_REVIEW') {
        return <button className="license-cell-btn" onClick={() => onReview(doc)}>Review</button>;
    }
    const statusValue = (doc.approval_status || '').toUpperCase();
    if (statusValue === 'APPROVED') {
        return <span className="license-cell license-cell--done">✓ Reviewed</span>;
    }
    if (statusValue === 'REJECTED') {
        return <span className="license-cell license-cell--rejected">✕ Rejected</span>;
    }
    return <span className="license-cell">{doc.approval_status || '—'}</span>;
}

function Modal({ doc, onClose, onAction }) {
    const [isRejecting, setIsRejecting] = useState(false);
    const [reason, setReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [currentDocIndex, setCurrentDocIndex] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(0.75);

    useEffect(() => {
        setCurrentDocIndex(0);
        setIsRejecting(false);
        setReason('');
        setCustomReason('');
        setZoomLevel(0.75);
    }, [doc.id]);

    useEffect(() => {
        setZoomLevel(0.75);
    }, [currentDocIndex]);

    const totalDocs = doc.document_urls.length;
    const currentDocPath = doc.document_urls[currentDocIndex];
    const isPdf = currentDocPath.toLowerCase().endsWith('.pdf');
    const baseUrl = process.env.REACT_APP_DEPLOYED_APP_URL || '';
    const docUrl = `${baseUrl}/${currentDocPath}`;

    const getFileNameFromPath = (path) => {
        const segment = path.split('/').pop() || path;
        const ext = segment.includes('.') ? segment.slice(segment.lastIndexOf('.')) : '.pdf';
        return segment && segment !== path ? segment : `document${ext}`;
    };

    const buildDocumentUrl = (path) => {
        if (typeof path !== 'string' || !path.trim()) return null;
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        const base = (baseUrl || '').replace(/\/$/, '');
        const relative = path.replace(/^\//, '');
        return base ? `${base}/${relative}` : relative;
    };

    const handleDownloadAll = async () => {
        if (isDownloading) return;
        setIsDownloading(true);

        const allDocs = Array.from(new Set([
            ...(doc.document_urls || []),
            ...(doc.document_acknowledgement_urls || []),
            ...(doc.document_acknowledgement_url ? [doc.document_acknowledgement_url] : [])
        ])).filter(url => typeof url === 'string' && url.trim().length > 0);

        try {
            if (allDocs.length === 1) {
                const path = allDocs[0];
                const url = buildDocumentUrl(path);
                if (!url) throw new Error('Invalid document URL');
                const res = await fetch(url, { mode: 'cors', credentials: 'same-origin' });
                if (!res.ok) throw new Error(`Download failed (${res.status})`);
                const blob = await res.blob();
        let name = getFileNameFromPath(path);
        // If the API provides a content-disposition header, use it to determine the filename
        const contentDisposition = res.headers.get('Content-Disposition');
        if (contentDisposition && contentDisposition.includes('filename=')) {
          name = contentDisposition.split('filename=')[1].split(';')[0].trim().replace(/"/g, ''); // Extract filename from header
        }
        console.log(doc,'------')
        const customerCode = Array.isArray(doc.sap_ids) ? doc.sap_ids[0] : doc.sap_ids;
        const segment = doc.document_license_type === 'PESTICIDE' ? 'IN09' : (doc.document_license_type === 'SEED' ? 'IN81' : null);
        const stateCode = doc.state_name;
        
        if (customerCode && segment && stateCode) {
            const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
            name = `${customerCode}_${segment}_${stateCode}${ext}`;
        }

                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = name;
                a.click();
                URL.revokeObjectURL(a.href);

            } else {
                const zip = new JSZip();
                let added = 0;

                const customerCode = Array.isArray(doc.sap_ids) ? doc.sap_ids[0] : doc.sap_ids;
                const segment = doc.document_license_type === 'PESTICIDE' ? 'IN09' : (doc.document_license_type === 'SEED' ? 'IN81' : null);
                const stateCode = doc.state_name;
                let baseCustomFileName = null;
                if (customerCode && segment && stateCode) {
                    baseCustomFileName = `${customerCode}_${segment}_${stateCode}`;
                }

                for (let i = 0; i < allDocs.length; i++) {
                    const path = allDocs[i];
                    const url = buildDocumentUrl(path);
                    if (!url) continue;
                    try {
                        const res = await fetch(url, { mode: 'cors', credentials: 'same-origin' });
                        if (!res.ok) continue;
                        const blob = await res.blob();
                        
                        let fileName = getFileNameFromPath(path);
                        const contentDisposition = res.headers.get('Content-Disposition');
                        if (contentDisposition && contentDisposition.includes('filename=')) {
                            fileName = contentDisposition.split('filename=')[1].split(';')[0].trim().replace(/"/g, '');
                        }
                        
                        if (baseCustomFileName) {
                            const ext = fileName.includes('.') ? fileName.slice(fileName.lastIndexOf('.')) : '';
                            fileName = `${baseCustomFileName}_${i + 1}${ext}`;
                        }
                        
                        // Ensure unique filenames in zip if multiple files have same name
                        zip.file(fileName, blob);
                        added += 1;
                    } catch (_) {
                        console.warn(`Could not fetch document ${i + 1}:`, url);
                    }
                }
                if (added === 0) {
                    throw new Error('Could not fetch any documents. They may be on a different origin (CORS).');
                }
                const zipBlob = await zip.generateAsync({ type: 'blob' });

                let zipName = 'documents.zip';
                if (customerCode && segment && stateCode) {
                    zipName = `${customerCode}_${segment}_${stateCode}.zip`;
                } else {
                    const tax = (doc.tax_number || 'documents').replace(/[^a-zA-Z0-9_-]/g, '_');
                    const state = (doc.state_code || doc.state_name || '').replace(/[^a-zA-Z0-9_-]/g, '_');
                    zipName = state ? `${tax}_${state}.zip` : `${tax}.zip`;
                }

                const a = document.createElement('a');
                a.href = URL.createObjectURL(zipBlob);
                a.download = zipName;
                a.click();
                URL.revokeObjectURL(a.href);
            }
        } catch (err) {
            console.error('Download failed', err);
            alert(err.message || 'Download failed. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };

    const handlePrev = () => {
        if (currentDocIndex > 0) {
            setCurrentDocIndex(currentDocIndex - 1);
        }
    };

    const handleNext = () => {
        if (currentDocIndex < totalDocs - 1) {
            setCurrentDocIndex(currentDocIndex + 1);
        }
    };

    const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.25, 1));
    const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.25, 0.5));

    const rejectionReasons = [
        "License photo/document not clear",
        "Invalid license document",
        "License # mismatch",
        "Incorrect license issue date",
        "Incorrect license expiry date",
        "License expired; upload valid license",
        "Others"
    ];

    const finalReason = reason === 'Others' ? customReason : reason;
    const isReasonValid = reason && (reason !== 'Others' || (reason === 'Others' && customReason.trim() !== ''));

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h3>Reviewing: {doc.document_license_type} License</h3>
                    <button type="button" className="close-x" onClick={onClose} aria-label="Close">&times;</button>
                </div>

                <div className="modal-body">
                    {/* LEFT: Preview */}
                    <div className="doc-preview-container">
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', paddingBottom: '0.5rem' }}>
                            <button type="button" onClick={handleZoomOut} style={{ background: 'none', border: 'none', cursor: 'pointer' }} title="Zoom Out">
                                <ZoomOut size={20} />
                            </button>
                            <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{Math.round(zoomLevel * 100)}%</span>
                            <button type="button" onClick={handleZoomIn} style={{ background: 'none', border: 'none', cursor: 'pointer' }} title="Zoom In">
                                <ZoomIn size={20} />
                            </button>
                        </div>
                        <div className="doc-preview" style={{ overflow: 'auto' }}>
                            {isPdf ? (
                                <iframe src={`${docUrl}#toolbar=0&view=FitH`} className="preview-frame" title="Document Preview" style={{ width: `${zoomLevel * 100}%` }} />
                            ) : (
                                <img src={docUrl} className="preview-img" alt="Document Preview" style={{ width: `${zoomLevel * 100}%`, maxWidth: 'none', maxHeight: 'none' }} />
                            )}
                        </div>
                        {totalDocs > 1 && (
                            <div className="preview-nav">
                                <button onClick={handlePrev} disabled={currentDocIndex === 0}>
                                    &lt; Prev
                                </button>
                                <span>{currentDocIndex + 1} of {totalDocs}</span>
                                <button onClick={handleNext} disabled={currentDocIndex === totalDocs - 1}>
                                    Next &gt;
                                </button>
                            </div>
                        )}
                    </div>

                    {/* RIGHT: Detailed Info */}
                    <div className="details-panel">
                        <h3>Document Details:</h3>
                        <div className="info-grid">
                            <p><strong>SAP ID(s):</strong> {Array.isArray(doc.sap_ids) ? doc.sap_ids.join(', ') : (doc.sap_ids || '—')}</p>
                            <p><strong>Distributor:</strong> {doc.distributor_name}</p>
                            <p><strong>State:</strong> {doc.state_name}</p>
                            <p><strong>PAN Number:</strong> {doc.tax_number || '—'}</p>
                            <p><strong>License Number:</strong> {doc.license_number}</p>
                            <p><strong>Issue Date:</strong> {doc.license_issue_date}</p>
                            <p><strong>Expiry Date:</strong> <span style={{
                                color: (doc.license_expiry_date && new Date(doc.license_expiry_date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)) ? 'red' : 'inherit'
                            }}>
                                {doc.license_expiry_date || 'N/A'}
                            </span></p>
                            {doc.license_reapplication_date && <p><strong>Re Application Date:</strong> {doc.license_reapplication_date}</p>}
                        </div>
                        {/* Action Buttons here... */}
                    </div>
                </div>

                <div className="modal-footer-custom">
                    {!isRejecting ? (
                        <div className="action-row">
                            <button
                                type="button"
                                className="modal-download-btn"
                                onClick={handleDownloadAll}
                                disabled={isDownloading}
                                title={totalDocs > 1 ? 'Download all documents as ZIP' : 'Download document'}
                            >
                                {isDownloading ? 'Downloading…' : totalDocs > 1 ? 'Download all (ZIP)' : 'Download'}
                            </button>
                            <button className="reject-btn-outline" onClick={() => setIsRejecting(true)}>Reject</button>
                            <button className="approve-btn" onClick={() => onAction(doc.id, 'APPROVED')}>Approve</button>
                        </div>
                    ) : (
                        <div className="rejection-form">
                            <select value={reason} onChange={(e) => setReason(e.target.value)}>
                                <option value="" disabled>Select a reason</option>
                                {rejectionReasons.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                            {reason === 'Others' && (
                                <textarea
                                    placeholder="Please specify reason (max 500 characters)"
                                    maxLength="500"
                                    value={customReason}
                                    onChange={(e) => setCustomReason(e.target.value)}
                                />
                            )}
                            <div className="rejection-actions">
                                <button type="button" className="secondary-btn" onClick={() => setIsRejecting(false)}>Cancel</button>
                                <button
                                    type="button"
                                    className="reject-confirm-btn"
                                    onClick={() => onAction(doc.id, 'REJECTED', finalReason)}
                                    disabled={!isReasonValid}>Confirm Rejection</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default GEGDashboard;